import QRCode from 'qrcode';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import OtpInput from 'react-otp-input';

import {
    useCreateTotpForUserMutation,
    useDeleteMfaConfigMutation,
    useTotpSetupOptionsForUserMutation,
} from '@/services/innovorder/auth/auth.endpoints';
import type { TotpMethod } from '@/services/innovorder/auth/auth.type';
import { getErrorCode, stringifyError } from '@/utils/errors';

import { BackupCodeItem, BackupCodesList, CopySecretButton, CodeInputWrapper, OtpBox } from '@/pages/Mfa/Mfa.style';

import {
    Actions,
    CredentialDetails,
    CredentialList,
    CredentialMeta,
    CredentialName,
    CredentialRow,
    EmptyState,
    ErrorText,
    Modal,
    ModalCard,
    Section,
    SectionDescription,
    StyledButton,
} from '../Security.style';

type Props = {
    userId: number;
    token: string;
    totp: TotpMethod[];
};

const TOTP_LENGTH = 6;
const OTP_CONTAINER_STYLE: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
};

const formatDate = (raw: string): string => {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString();
};

const TotpSection = ({ userId, token, totp }: Props): ReactElement => {
    const intl = useIntl();
    const [deleteConfig] = useDeleteMfaConfigMutation();
    const [getSetupOptions, { data: setupData, error: setupError, isLoading: setupLoading, reset: resetSetup }] =
        useTotpSetupOptionsForUserMutation();
    const [createTotp, { data: createdData, error: createError, isLoading: creating, reset: resetCreate }] =
        useCreateTotpForUserMutation();

    const [modalOpen, setModalOpen] = useState(false);
    const [code, setCode] = useState('');
    const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
    const [secretCopied, setSecretCopied] = useState(false);
    const [shownBackupCodes, setShownBackupCodes] = useState<string[] | null>(null);

    const totpSecret = useMemo(() => {
        if (!setupData?.qrCodeUri) return null;
        try {
            return new URL(setupData.qrCodeUri).searchParams.get('secret');
        } catch {
            return null;
        }
    }, [setupData?.qrCodeUri]);

    useEffect(() => {
        if (!setupData?.qrCodeUri) {
            setQrDataUrl(null);
            return;
        }
        let cancelled = false;
        QRCode.toDataURL(setupData.qrCodeUri, { width: 200, margin: 1 }).then((dataUrl) => {
            if (!cancelled) setQrDataUrl(dataUrl);
        });
        return () => {
            cancelled = true;
        };
    }, [setupData?.qrCodeUri]);

    // After a successful create the API surfaces backup codes only on first MFA enrolment.
    useEffect(() => {
        if (!createdData) return;
        if (createdData.backupCodes && createdData.backupCodes.length > 0) {
            setShownBackupCodes(createdData.backupCodes);
        }
        setModalOpen(false);
        setCode('');
    }, [createdData]);

    const handleAdd = useCallback(() => {
        resetSetup();
        resetCreate();
        setCode('');
        setQrDataUrl(null);
        setModalOpen(true);
        getSetupOptions({ token, userId });
    }, [getSetupOptions, resetSetup, resetCreate, token, userId]);

    const handleClose = useCallback(() => {
        setModalOpen(false);
        setCode('');
    }, []);

    const handleCopySecret = useCallback(() => {
        if (!totpSecret) return;
        navigator.clipboard.writeText(totpSecret).then(() => {
            setSecretCopied(true);
            window.setTimeout(() => setSecretCopied(false), 1500);
        });
    }, [totpSecret]);

    const handleOtpChange = useCallback(
        (next: string): void => {
            setCode(next);
            if (next.length === TOTP_LENGTH && setupData?.setupId && !creating) {
                createTotp({ token, userId, code: next, setupId: setupData.setupId });
            }
        },
        [createTotp, creating, setupData?.setupId, token, userId],
    );

    const handleRemove = useCallback(
        async (configId: number) => {
            const confirmed = window.confirm(intl.formatMessage({ id: 'security.totp.confirmRemove' }));
            if (!confirmed) return;
            await deleteConfig({ token, configId });
        },
        [deleteConfig, intl, token],
    );

    const createErrorCode = createError ? getErrorCode(createError) : null;
    const modalErrorLabel =
        createErrorCode === 'mfa_verification_failed' ? (
            <FormattedMessage id="mfa.setup.invalidCode" />
        ) : createErrorCode === 'mfa_token_expired' ? (
            <FormattedMessage id="mfa.verify.tokenExpired" />
        ) : (
            stringifyError(setupError) || stringifyError(createError)
        );

    return (
        <Section>
            <h2>
                <FormattedMessage id="security.totp.title" />
            </h2>
            <SectionDescription>
                <FormattedMessage id="security.totp.subtitle" />
            </SectionDescription>

            {totp.length === 0 ? (
                <EmptyState>
                    <FormattedMessage id="security.totp.empty" />
                </EmptyState>
            ) : (
                <CredentialList>
                    {totp.map((c) => (
                        <CredentialRow key={c.userMfaConfigId}>
                            <CredentialMeta>
                                <CredentialName>
                                    <FormattedMessage id="security.totp.label" />
                                </CredentialName>
                                <CredentialDetails>
                                    <FormattedMessage
                                        id="security.totp.added"
                                        values={{ date: formatDate(c.createdAt) }}
                                    />
                                </CredentialDetails>
                            </CredentialMeta>
                            <StyledButton
                                buttonType="secondary"
                                onClick={(): Promise<void> => handleRemove(c.userMfaConfigId)}
                            >
                                <FormattedMessage id="security.actions.remove" />
                            </StyledButton>
                        </CredentialRow>
                    ))}
                </CredentialList>
            )}

            {totp.length === 0 ? (
                <Actions>
                    <StyledButton buttonType="primary" onClick={handleAdd}>
                        <FormattedMessage id="security.totp.add" />
                    </StyledButton>
                </Actions>
            ) : null}

            {modalOpen ? (
                <Modal role="dialog" aria-modal="true">
                    <ModalCard>
                        <h2>
                            <FormattedMessage id="security.totp.modalTitle" />
                        </h2>
                        <SectionDescription>
                            <FormattedMessage id="mfa.setup.subtitle" />
                        </SectionDescription>

                        <div style={{ display: 'flex', justifyContent: 'center', margin: '8px 0 16px' }}>
                            {setupLoading && !qrDataUrl ? (
                                <FormattedMessage id="mfa.setup.loadingQr" />
                            ) : qrDataUrl ? (
                                <img src={qrDataUrl} alt="TOTP QR code" width={200} height={200} />
                            ) : null}
                        </div>

                        {totpSecret ? (
                            <CopySecretButton type="button" onClick={handleCopySecret}>
                                <FormattedMessage id={secretCopied ? 'mfa.setup.copied' : 'mfa.setup.copySecret'} />
                            </CopySecretButton>
                        ) : null}

                        <CodeInputWrapper>
                            <OtpInput
                                value={code}
                                onChange={handleOtpChange}
                                numInputs={TOTP_LENGTH}
                                inputType="tel"
                                skipDefaultStyles
                                containerStyle={OTP_CONTAINER_STYLE}
                                renderInput={(props): React.ReactElement => <OtpBox {...props} />}
                            />
                        </CodeInputWrapper>

                        {modalErrorLabel ? <ErrorText>{modalErrorLabel}</ErrorText> : null}

                        <Actions>
                            <StyledButton buttonType="secondary" onClick={handleClose}>
                                <FormattedMessage id="security.actions.cancel" />
                            </StyledButton>
                        </Actions>
                    </ModalCard>
                </Modal>
            ) : null}

            {shownBackupCodes ? (
                <Modal role="dialog" aria-modal="true">
                    <ModalCard>
                        <h2>
                            <FormattedMessage id="mfa.setup.backupTitle" />
                        </h2>
                        <p>
                            <FormattedMessage id="mfa.setup.backupSubtitle" />
                        </p>
                        <BackupCodesList>
                            {shownBackupCodes.map((c) => (
                                <BackupCodeItem key={c}>{c}</BackupCodeItem>
                            ))}
                        </BackupCodesList>
                        <StyledButton buttonType="primary" onClick={(): void => setShownBackupCodes(null)}>
                            <FormattedMessage id="mfa.setup.backupAcknowledge" />
                        </StyledButton>
                    </ModalCard>
                </Modal>
            ) : null}
        </Section>
    );
};

export default TotpSection;
